import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { StaffCombobox } from "@/components/StaffCombobox";
import { Users, AlertTriangle, Briefcase, ListChecks, ArrowRightLeft, Plus, Search, GripVertical, Repeat, AlarmClock, TreePalm, Send, CalendarRange } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { RecurringActions } from "@/components/tasks/RecurringActions";
import { fmtRange } from "@/components/tasks/OutOfOfficeDialog";

const fmtDue = (iso) => { try { return new Date(iso + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" }); } catch { return iso; } };
const initials = (n) => (n || "?").split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
const DRAG_TYPE = "application/x-monge-task";

function ReassignPopover({ task, onMove }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="text-muted-foreground hover:text-primary shrink-0" title="Reassign" data-testid={`board-reassign-${task.id}`}><ArrowRightLeft className="h-3.5 w-3.5" /></button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3" align="end">
        <div className="text-xs font-semibold mb-2">Reassign “{task.title}” to…</div>
        <StaffCombobox testid={`board-reassign-pick-${task.id}`} value="" placeholder="Pick a person" onChange={(name, email) => { if (email) { setOpen(false); onMove(task, { name, email }); } }} />
      </PopoverContent>
    </Popover>
  );
}

function TaskItem({ t, today, personEmail, onMove, onChanged, onDragState }) {
  const navigate = useNavigate();
  const overdue = t.due && t.due < today;
  const draggable = t.kind === "task";
  return (
    <li draggable={draggable} data-testid={`board-item-${t.kind}`} data-task-id={t.id}
      onDragStart={(e) => { if (!draggable) return; e.dataTransfer.setData(DRAG_TYPE, JSON.stringify({ id: t.id, title: t.title, from: personEmail })); e.dataTransfer.effectAllowed = "move"; onDragState(true); }}
      onDragEnd={() => onDragState(false)}
      className={`flex items-center gap-2 px-3 py-2 text-sm ${draggable ? "cursor-grab active:cursor-grabbing" : ""}`}>
      {draggable ? <GripVertical className="h-3.5 w-3.5 text-muted-foreground/60 shrink-0" /> : <Briefcase className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
      {draggable && <ListChecks className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
      <button onClick={() => (t.kind === "matter" ? navigate("/matter") : null)} className={`flex-1 min-w-0 text-left ${t.kind === "matter" ? "hover:text-primary" : "cursor-default"}`}>
        <span className="block truncate">{t.title}{t.repeat && <Repeat className="inline h-3 w-3 ml-1 text-primary align-[-1px]" title={`Repeats ${t.repeat}`} />}{t.snoozed_from && <AlarmClock className="inline h-3 w-3 ml-1 text-amber-600 align-[-1px]" title={`Snoozed (originally due ${fmtDue(t.snoozed_from)})`} data-testid="board-snoozed" />}</span>
        {t.matter_label && <span className="block text-[11px] text-muted-foreground truncate">{t.matter_label}</span>}
      </button>
      {t.due && <span className={`text-[11px] whitespace-nowrap ${overdue ? "text-destructive font-medium" : "text-muted-foreground"}`}>{fmtDue(t.due)}</span>}
      {draggable && t.repeat && <RecurringActions task={t} onChanged={onChanged} />}
      {draggable && <ReassignPopover task={t} onMove={onMove} />}
    </li>
  );
}

function PersonCard({ p, maxOpen, today, onMove, onAddFor, onChanged, onReroute, dragging, onDragState }) {
  const [over, setOver] = useState(false);
  const pct = maxOpen ? Math.round((p.open / maxOpen) * 100) : 0;
  const barCls = p.overdue ? "bg-destructive" : pct > 66 ? "bg-amber-500" : "bg-primary";
  const onDrop = (e) => {
    e.preventDefault(); setOver(false);
    try {
      const data = JSON.parse(e.dataTransfer.getData(DRAG_TYPE) || "{}");
      if (data.id && data.from !== p.email) onMove({ id: data.id, title: data.title }, { name: p.name, email: p.email });
    } catch (err) { console.error("drop", err); }
  };
  return (
    <div data-testid={`board-person-${p.email}`}
      onDragOver={(e) => { if (e.dataTransfer.types.includes(DRAG_TYPE)) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setOver(true); } }}
      onDragLeave={() => setOver(false)} onDrop={onDrop}
      className={`border bg-card rounded-sm flex flex-col transition-colors ${over ? "border-primary ring-2 ring-primary/30 bg-primary/[0.03]" : dragging ? "border-dashed border-primary/40" : "border-border"} ${p.ooo?.status === "away" ? "border-l-2 border-l-amber-500" : ""}`}>
      <div className="p-3 border-b border-border">
        <div className="flex items-center gap-2.5">
          <span className="h-8 w-8 rounded-full bg-primary/10 text-primary grid place-items-center text-xs font-bold shrink-0">{initials(p.name)}</span>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold truncate">{p.name}</div>
            <div className="text-[11px] text-muted-foreground truncate">{p.email}</div>
          </div>
          <button onClick={() => onAddFor(p)} className="text-muted-foreground hover:text-primary" title={`Add task for ${p.name}`} data-testid={`board-add-${p.email}`}><Plus className="h-4 w-4" /></button>
        </div>
        {p.ooo && (
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <div className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-0.5 rounded-sm ${p.ooo.status === "away" ? "bg-amber-100 text-amber-800" : "bg-secondary text-muted-foreground"}`} title={p.ooo.note || undefined} data-testid={`board-ooo-${p.email}`}>
              <TreePalm className="h-3 w-3" />
              {p.ooo.status === "away" ? `Away · back ${fmtDue(p.ooo.back)}` : `Out ${fmtRange(p.ooo.start, p.ooo.end)}`}
            </div>
            {p.ooo.backup_email && p.tasks.some((t) => t.kind === "task") && (
              <button onClick={() => onReroute(p)} className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-sm bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground transition-colors" title={`Hand ${p.name}'s open tasks to ${p.ooo.backup_name}`} data-testid={`board-reroute-${p.email}`}>
                <Send className="h-3 w-3" /> Send tasks to {p.ooo.backup_name}
              </button>
            )}
          </div>
        )}
        <div className="mt-2.5 flex items-center gap-3 text-xs">
          <span className="font-semibold" data-testid={`board-open-${p.email}`}>{p.open} open</span>
          {p.overdue > 0 && <span className="text-destructive font-medium inline-flex items-center gap-1"><AlertTriangle className="h-3 w-3" />{p.overdue} overdue</span>}
          {p.soon > 0 && <span className="text-amber-600">{p.soon} this week</span>}
        </div>
        <div className="mt-1.5 h-1.5 bg-secondary rounded-sm overflow-hidden"><div className={`h-full ${barCls}`} style={{ width: `${Math.max(4, pct)}%` }} /></div>
      </div>
      <ul className="divide-y divide-border max-h-80 overflow-y-auto min-h-[44px]">
        {p.tasks.length === 0 && <li className="px-3 py-3 text-xs text-muted-foreground text-center">{over ? "Drop to assign" : "Nothing open"}</li>}
        {p.tasks.map((t) => <TaskItem key={`${t.kind}-${t.id}-${t.title}`} t={t} today={today} personEmail={p.email} onMove={onMove} onChanged={onChanged} onDragState={onDragState} />)}
      </ul>
    </div>
  );
}

export function TeamBoard({ onAddFor, onCoverage, refreshKey }) {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [dragging, setDragging] = useState(false);
  const load = useCallback(() => api.get("/tasks/board").then((r) => setData(r.data)).catch((e) => console.error("board", e)), []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const move = async (task, to) => {
    try { await api.patch(`/tasks/${task.id}`, { assignee_email: to.email, assignee_name: to.name }); toast.success(`“${task.title}” → ${to.name}`); load(); }
    catch (e) { console.error("reassign", e); toast.error(e?.response?.data?.detail || "Could not reassign"); }
  };
  const reroute = async (p) => {
    try {
      const { data } = await api.post(`/ooo/${p.ooo.id}/reroute-tasks`);
      toast.success(`${data.rerouted} task${data.rerouted === 1 ? "" : "s"} sent to ${data.backup_name}`); load();
    } catch (e) { console.error("reroute", e); toast.error(e?.response?.data?.detail || "Could not reroute"); }
  };

  if (!data) return <div className="h-48 border border-border rounded-sm animate-pulse bg-secondary/40" data-testid="board-loading" />;
  const needle = q.trim().toLowerCase();
  const people = needle ? data.people.filter((p) => p.name.toLowerCase().includes(needle) || p.email.includes(needle)) : data.people;
  const maxOpen = Math.max(0, ...data.people.map((p) => p.open));
  return (
    <div data-testid="team-board">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Users className="h-4 w-4 text-primary" />
        <span className="text-sm font-semibold">Team workload</span>
        <span className="text-xs text-muted-foreground" data-testid="board-totals">{data.totals.people} people · {data.totals.open} open items · <span className={data.totals.overdue ? "text-destructive font-medium" : ""}>{data.totals.overdue} overdue</span></span>
        <span className="text-[11px] text-muted-foreground hidden md:inline">· drag a task onto a person to reassign it</span>
        <div className="relative ml-auto w-60">
          <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a person…" className="pl-8 h-8" data-testid="board-search" />
        </div>
        <Button variant="outline" size="sm" onClick={onCoverage} data-testid="board-coverage-btn"><CalendarRange className="h-3.5 w-3.5 mr-1.5" /> Coverage</Button>
        <Button variant="outline" size="sm" onClick={load} data-testid="board-refresh">Refresh</Button>
      </div>
      {(data.away?.length > 0 || data.upcoming?.length > 0) && (
        <div className="flex flex-wrap items-center gap-2 mb-4 px-3 py-2 rounded-sm border border-amber-300 bg-amber-50 text-sm" data-testid="board-away">
          <TreePalm className="h-4 w-4 text-amber-700 shrink-0" />
          <span className="font-semibold text-amber-800">{data.away.length ? `Away today (${data.away.length}):` : "No one away today."}</span>
          {data.away.map((o) => <span key={o.id} className="text-amber-800" title={o.note || undefined} data-testid={`board-away-${o.user_email}`}>{o.user_name} <span className="text-amber-700/80">· back {fmtDue(o.back)}</span></span>)}
          {data.upcoming?.length > 0 && <span className="text-xs text-muted-foreground ml-auto" data-testid="board-upcoming">{data.upcoming.length} scheduled out in the next 30 days: {data.upcoming.map((o) => `${o.user_name} (${fmtRange(o.start, o.end)})`).join(", ")}</span>}
        </div>
      )}
      {people.length === 0 ? (
        <p className="text-sm text-muted-foreground border border-dashed border-border rounded-sm p-6 text-center" data-testid="board-empty">{data.people.length ? "No one matches that search." : "No open work assigned to anyone yet — add a task with New Task."}</p>
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {people.map((p) => <PersonCard key={p.email} p={p} maxOpen={maxOpen} today={data.today} onMove={move} onAddFor={onAddFor} onChanged={load} onReroute={reroute} dragging={dragging} onDragState={setDragging} />)}
        </div>
      )}
    </div>
  );
}
